import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import RightModal from "../../shared/RightModal";
import ModalHeaderComponent from "../../shared/ModalHeaderComponent";
import ModalBodyComponent from "../../shared/ModalBodyComponent";

import ChatbotOptionModel from '../ChatbotOptionModel';
import { CHATBOT_OPTIONS_TYPE } from '../../../constants/AppConstants';
import CloseIcon from "../../icons/CloseIcon";
import PreviewFile from "../../shared/PreviewFile";

const PreviewBot = ({
  chatbot,
  option,
  toggleModal
}) => {
  const { t } = useTranslation();
  const [pathOptions, setPathOptions] = useState([]);
  const [optionList, setOptionlist] = useState(null);

  useEffect(() => {
    ChatbotOptionModel.getPathOptions(chatbot.web_id, option.id).then((res) => {
      setPathOptions(res.option.data);
    });
  }, [option]);

  const buildConversation = () => {
    if (isEmpty(pathOptions)) return;

    const html = pathOptions.map((opt, index) => {
      const { fake_option } = opt.attributes;

      if (fake_option) {
        return false;
      }

      let incomingMessage = buildMessageToBot(opt);

      return (
        <div key={`message-flow-${index}`}>
          <div className="d-flex w-100 mt-10">
            <div className="sent-message-preview">
              {incomingMessage}
            </div>
          </div>
          {renderOuboundMsj(opt, index)}
        </div>
      );
    });

    return html;
  };

  const buildMessageToBot = (opt) => {
    const { option_type } = opt.attributes;

    if (option_type == CHATBOT_OPTIONS_TYPE.MESSAGE) {
      return buildSimpleMessage(opt);
    }

    if (option_type == CHATBOT_OPTIONS_TYPE.MENU) {
      return buildMenuMessage(opt);
    }

    if (option_type == CHATBOT_OPTIONS_TYPE.QUESTION) {
      return buildQuestionMessage(opt);
    }

    if (option_type == CHATBOT_OPTIONS_TYPE.WHATSAPP_FLOW || option_type == 'whatsapp_flow') {
      return buildWhatsappFlowMessage(opt);
    }

    return false;
  };

  const buildWhatsappFlowMessage = (opt) => {
    const { answer, text, whatsapp_flow } = opt.attributes;
    const flowName = whatsapp_flow?.name || text || 'WhatsApp Flow';
    const flowCta = whatsapp_flow?.default_flow_cta || opt.attributes.button_title || t('chatbot.config.preview.open_flow');
    const bodyText = answer || whatsapp_flow?.default_body_text || t('chatbot.config.preview.whatsapp_flow_body');

    return (
      <div className="message-container bg-white">
        <div className="text-pre-line fs-md-and-down-12">
          <div className="font-weight-bold mb-5">{flowName}</div>
          <div>{bodyText}</div>
        </div>
        <div className="new-divider-line float mt-10" />
        <div className="qr-list-options pt-10 text-center color-blue">
          <i className="mi-whatsapp-line mr-5" aria-hidden="true"></i>
          <span>{flowCta}</span>
        </div>
      </div>
    );
  };

  const buildMenuMessage = (opt) => {
    const { interactive, answer, children, button_title, selection_title } = opt.attributes;

    let options = [];
    let newOption;

    if (interactive == 'text') {
      options = children.map((child) => {
        return { position: child.position, text: child.text };
      });
    } else if (interactive == 'buttons') {
      options = children;
    } else if (interactive == 'list') {
      newOption = {
        button_title,
        selection_title,
        options: children.map((child) => {
          return {
            position: child.position,
            text: child.text,
            list_item_description: child.list_item_description
          };
        })
      };
    }

    return (
      <>
        <div className="message-container bg-white">
          <div className="text-pre-line fs-md-and-down-12 bg-white">
            <div>{answer}</div>
          </div>

          {interactive == 'text' && (
            <>{buildOptionWithNumbers(options)}</>
          )}

          {interactive == 'list' && (
            <>{buildListMessage(newOption)}</>
          )}
        </div>
        {interactive == 'buttons' && (
          <>{buildButtonMessage(options)}</>
        )}
      </>
    );
  };

  const buildButtonMessage = (options) => {
    return (
      <div className="text-pre-line fs-md-and-down-12 border-8">
        {options.map((opt, index) => (
          <div key={`button-option-${index}`} className="qr-list-options">
            <span>{opt.text}</span>
          </div>
        ))}
      </div>
    );
  };

  const buildListMessage = (newOption) => {
    return (
      <>
        <div className="new-divider-line float mt-10" />
        <div className="cursor-pointer qr-list-options pt-20" onClick={() => setOptionlist(newOption)}>
          <i className="fa fa-list-ul mr-5" aria-hidden="true"></i>
          <span>{newOption.button_title}</span>
        </div>
      </>
    );
  };

  const buildQuestionMessage = (opt) => {
    const { answer, interactive, button_title, selection_title, option_sub_list } = opt.attributes;
    let options = [];
    let newOption;

    if (interactive == 'text') {
      options = option_sub_list.map((optSubList) => {
        return { position: optSubList.position, text: optSubList.value_to_show };
      });
    } else if (interactive == 'buttons') {
      options = option_sub_list.map((optSubList) => {
        return { position: optSubList.position, text: optSubList.value_to_show };
      });
    } else if (interactive == 'list') {
      newOption = {
        button_title,
        selection_title,
        options: option_sub_list.map((optSubList) => {
          return {
            position: optSubList.position,
            text: optSubList.value_to_show,
            list_item_description: optSubList.list_item_description
          };
        })
      };
    }

    return (
      <>
        <div className="message-container bg-white">
          <div className="text-pre-line fs-md-and-down-12">
            <div>{answer}</div>
          </div>

          {interactive == 'text' && (
            <>{buildOptionWithNumbers(options)}</>
          )}

          {interactive == 'list' && (
            <>{buildListMessage(newOption)}</>
          )}
        </div>
        {interactive == 'buttons' && (
          <>{buildButtonMessage(options)}</>
        )}
      </>
    );
  };

  const buildOptionWithNumbers = (options) => {
    return (
      <div className="d-flex flex-column mt-10">
        {options.map((opt, index) => (
          <div key={`option-number-${index}`}>
            <span>
              <b>{opt.position}.</b> {opt.text}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const buildSimpleMessage = (opt) => {
    const { type, answer, attached_file } = opt.attributes;

    if (type == 'text') {
      return (
        <div className="message-container bg-white">
          <div className="text-pre-line fs-md-and-down-12">{answer}</div>
        </div>
      );
    }

    const { width, height } = attached_file.metadata;

    return (
      <div className="border-10 bg-white">
        <div className="template-example bg-white">
          <PreviewFile
            attachedFile={attached_file}
            imageSize={{ width, height }}
            direction='in'
          />
        </div>
        {answer && (
          <div className="text-pre-line fs-md-and-down-12 p-8">
            <div>{answer}</div>
          </div>
        )}
      </div>
    );
  };

  const renderOuboundMsj = (opt, index) => {
    const { option_type, jump_to_option } = opt.attributes;

    if (option_type == CHATBOT_OPTIONS_TYPE.MESSAGE) { return; }

    if ((pathOptions.length - 1) >= (index + 1) && (option_type == CHATBOT_OPTIONS_TYPE.QUESTION || !jump_to_option)) {
      return (
        <div className="my-10 received-message-preview text-right">
          {insertPreviewResponse(opt, pathOptions[index + 1])}
        </div>
      );
    }
  };

  const insertPreviewResponse = (opt, nextOpt) => {
    if (opt.attributes.option_type == CHATBOT_OPTIONS_TYPE.QUESTION) {
      return (
        <span>{t('chatbot.config.preview.customer_sends_response')}</span>
      );
    }
    return (
      <span>{nextOpt.attributes.position}</span>
    );
  };

  return (
    <RightModal>
      <div className="mercately-modal">
        <ModalHeaderComponent title={t('chatbot.config.preview.title')} toggleModal={toggleModal} />
        <ModalBodyComponent>
          <div className="col-md-12">
            <fieldset className="fieldset mr-0 ml-0 new-chat-preview-container">
              <div className="phone-container mt-10 mr-0">
                <div className="header-phone py-8">
                  <div className="ml-15 fs-14 c-black">{t('chatbot.config.preview.flow')}</div>
                  <div className="ml-15 c-gray-label fs-10">{t('chatbot.config.preview.online')}</div>
                </div>

                <div id="chat-preview" className="conversation-container">
                  {buildConversation()}
                </div>

                {optionList && (
                  <div className="bot-preview-list-options">
                    <div className="d-flex d-flex bg-blue px-20 py-10">
                      <a className="mr-10" type="button" onClick={() => setOptionlist(null)}>
                        <CloseIcon className="fill-white" />
                      </a>
                      <span className="text-white fz-16">{optionList.selection_title}</span>
                    </div>
                    <div className="modal-body px-0 px-20">
                      <div className="container-fluid-no-padding">
                        <div className="col-md-12 px-0">
                          <span className="fz-18 text-blue">{t('chatbot.config.preview.select_option')}</span>
                        </div>
                        <div className="col-md-12 px-0 mt-10">
                          {optionList.options.map((opt, index) => (
                            <div key={`list-option-${index}`} className="mt-10">
                              <span className="c-grey-light fz-16">{opt.text}</span>
                              {opt.list_item_description ? (
                                <div className="c-gray-label fz-12 mt-4">{opt.list_item_description}</div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </fieldset>
          </div>
        </ModalBodyComponent>
        <div className="modal__footer top-line">
          <div className="px-20 mt-20 d-flex justify-content-center">
            <button type="button"
              onClick={toggleModal}
              className="btn-btn btn-secondary-style bg-light col-md-10 col-sm-10 col-xs-12 px-0"
              style={{ border: '0px' }}>
              {t('chatbot.config.preview.close')}
            </button>
          </div>
        </div>
      </div>
    </RightModal>
  );
};

export default PreviewBot;
